// @flow

import Raven from "raven-js"
import { action, observable, runInAction } from "mobx"
import { isProductionApi } from "@cabonline/shared/utils/env"
import FleetGateway from "shared/apis/FleetGateway"
import type { Driver } from "../types/DriverType"
import { messageError } from "../utils/errors"

const handleError = error => messageError("driverWebStore", error)
class DriverStore {
  @observable drivers: Array<Object> = []
  @observable driver: ?Driver = null
  @observable token: string = ""
  @observable currentAverage = 0
  @observable numberOfLatestRatings = 0
  @observable tripDistributions: Array<Object> = []
  @observable courses: Array<Object> = []
  @observable sso: Object = {}
  @observable phoneNumberVerificationToken: Object = {}
  @observable isBulkSendingSMS: boolean = false
  @observable isBulkVerify: boolean = false

  @action
  async verifyPhoneNumber(verificationToken: string, licenseId: string) {
    try {
      return await FleetGateway.drivers.verifyPhoneNumber(
        verificationToken,
        licenseId
      )
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async getDriverPhoneNumberByToken(token: string) {
    try {
      return await FleetGateway.drivers.getDriverPhoneNumberByToken(token)
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async addDriverToSystemUser(verificationToken: string, email: string) {
    try {
      return await FleetGateway.drivers.addDriverToSystemUser(
        verificationToken,
        email
      )
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async createSystemUser(
    verificationToken: string,
    email: string,
    password: string
  ) {
    try {
      return await FleetGateway.drivers.createSystemUser(
        verificationToken,
        email,
        password
      )
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async sendSMSToDriver(driverId: string) {
    try {
      await FleetGateway.drivers.sendSMSToDriver(driverId)
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async bulkSendSMSToDrivers(driverIds: number[]) {
    runInAction(() => (this.isBulkSendingSMS = true))
    try {
      await FleetGateway.drivers.bulkSendSMSToDrivers(driverIds)
    } catch (e) {
      Raven.captureException(e)
      throw e
    } finally {
      runInAction(() => (this.isBulkSendingSMS = false))
    }
  }

  @action
  async bulkVerifyDrivers(driverIds: number[], permitCheckCorrect: boolean) {
    runInAction(() => (this.isBulkVerify = true))
    try {
      await FleetGateway.drivers.bulkVerifyDrivers(
        driverIds,
        permitCheckCorrect
      )
    } catch (e) {
      Raven.captureException(e)
      throw e
    } finally {
      runInAction(() => (this.isBulkVerify = false))
    }
  }

  @action
  async getLoggedInDriver(activeFleetsOnly?: boolean = false) {
    try {
      const result = await FleetGateway.drivers.getLoggedInDriver(
        activeFleetsOnly
      )
      runInAction(() => {
        this.driver = result.data
      })
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async updateLoggedInDriver(driver: Object) {
    if (!driver) return
    try {
      const response = await FleetGateway.drivers.updateLoggedInDriver(driver)
      runInAction(() => {
        this.driver = response.data
      })
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  getLoggedInDriverAverageRating = async () => {
    try {
      const data = await FleetGateway.drivers.getLoggedInDriverAverageRating()
      runInAction(() => {
        this.currentAverage = data.currentAverage
        this.numberOfLatestRatings =
          data.numberOfLatestRatingsThatAverageDriverRatingsAreBasedOn
      })
    } catch (e) {
      Raven.captureException(e)
      handleError(e)
    }
  }

  @action
  getDriverTripDistribution = async () => {
    try {
      const data = await FleetGateway.drivers.getLoggedInDriverTripDistribution()
      runInAction(() => {
        this.tripDistributions = data.tripDistributions
      })
    } catch (e) {
      Raven.captureException(e)
      handleError(e)
    }
  }

  @action
  async getListOfCourses(id: string) {
    try {
      const response = await FleetGateway.drivers.getListOfCourses(id)
      runInAction(() => {
        this.courses = response.data
      })
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  @action
  async getSSOForDriver() {
    try {
      const response = await FleetGateway.drivers.getSSOForDriver()
      runInAction(() => {
        this.sso = response.data
      })
    } catch (e) {
      Raven.captureException(e)
      throw e
    }
  }

  getLinkToTicTac({ data, sig }: { data: string, sig: string }) {
    const PROD_LINK = `http://cabonline.onlineacademy.se/sso/login/cabonline?data=${data}&sig=${sig}`
    const DEV_LINK = `http://cabonline.dev.onlineacademy.se/sso/login/cabonline?data=${data}&sig=${sig}`
    return isProductionApi ? PROD_LINK : DEV_LINK
  }

  @action
  async getPhoneNumberVerificationToken(driverId: string) {
    try {
      const response = await FleetGateway.drivers.getPhoneNumberVerificationToken(
        driverId
      )
      runInAction(() => {
        this.phoneNumberVerificationToken = response.data
      })
    } catch (e) {
      Raven.captureException(e)
      runInAction(() => {
        this.phoneNumberVerificationToken = {}
      })
      throw e
    }
  }

  async sendPhoneNumberReverification() {
    try {
      return await FleetGateway.drivers.reverifyPhoneNumber()
    } catch (e) {
      Raven.captureException(e)
    }
  }
}

export default (new DriverStore(): any)
